// Expression parser for the calculator tool. Hand-written recursive descent
// rather than eval() so untrusted input can never reach the JS engine.
//
// Percent follows the convention people expect from a pocket calculator:
// `200 + 10%` is 220 (ten percent *of the left operand*), while `200 * 10%`
// is 20 (a plain hundredth). Standalone `10%` is 0.1.

const APOSTROPHE_FREE_OPERATORS = {
  '×': '*',
  '·': '*',
  '÷': '/',
  '−': '-',
  '–': '-',
  ',': '.',
};

const isDigit = (ch) => ch >= '0' && ch <= '9';

export class CalcError extends Error {
  constructor(code) {
    super(code);
    this.code = code; // 'syntax' | 'divzero' | 'domain'
  }
}

const tokenize = (input) => {
  const tokens = [];
  const src = String(input);

  for (let i = 0; i < src.length;) {
    const raw = src[i];
    const ch = APOSTROPHE_FREE_OPERATORS[raw] || raw;

    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '_') { i += 1; continue; }

    if (isDigit(ch) || ch === '.') {
      let literal = '';
      while (i < src.length) {
        const c = APOSTROPHE_FREE_OPERATORS[src[i]] || src[i];
        if (!isDigit(c) && c !== '.') break;
        literal += c;
        i += 1;
      }
      if (literal === '.' || literal.split('.').length > 2) throw new CalcError('syntax');
      tokens.push({ type: 'number', value: Number(literal) });
      continue;
    }

    if (/[a-zπ]/i.test(ch)) {
      let name = '';
      while (i < src.length && /[a-zπ]/i.test(src[i])) { name += src[i]; i += 1; }
      tokens.push({ type: 'name', value: name.toLowerCase() });
      continue;
    }

    if ('+-*/%^()'.includes(ch) || ch === '√') {
      tokens.push({ type: ch });
      i += 1;
      continue;
    }

    throw new CalcError('syntax');
  }

  return tokens;
};

const CONSTANTS = { pi: Math.PI, π: Math.PI, e: Math.E };

const FUNCTIONS = {
  sqrt: (x) => { if (x < 0) throw new CalcError('domain'); return Math.sqrt(x); },
  cbrt: Math.cbrt,
  abs: Math.abs,
  ln: (x) => { if (x <= 0) throw new CalcError('domain'); return Math.log(x); },
  log: (x) => { if (x <= 0) throw new CalcError('domain'); return Math.log10(x); },
  sin: Math.sin,
  cos: Math.cos,
  tan: Math.tan,
  round: Math.round,
  floor: Math.floor,
  ceil: Math.ceil,
};

// expr    := term (('+' | '-') term)*
// term    := unary (('*' | '/' | 'mod') unary)*
// unary   := ('-' | '+' | '√') unary | power        -- binds looser than '^', so -3^2 is -9
// power   := postfix ('^' unary)?                   -- right associative, allows 2^-3
// postfix := primary '%'*
// primary := number | constant | fn '(' expr ')' | '(' expr ')'
const parse = (tokens) => {
  let pos = 0;
  const peek = () => tokens[pos];
  const eat = (type) => {
    if (!tokens[pos] || tokens[pos].type !== type) throw new CalcError('syntax');
    return tokens[pos++];
  };

  const parseExpr = () => {
    let node = parseTerm();
    while (peek() && (peek().type === '+' || peek().type === '-')) {
      const op = tokens[pos++].type;
      node = { type: 'bin', op, left: node, right: parseTerm() };
    }
    return node;
  };

  const parseTerm = () => {
    let node = parseUnary();
    while (peek() && (peek().type === '*' || peek().type === '/'
      || (peek().type === 'name' && peek().value === 'mod'))) {
      const token = tokens[pos++];
      const op = token.type === 'name' ? 'mod' : token.type;
      node = { type: 'bin', op, left: node, right: parseUnary() };
    }
    return node;
  };

  const parseUnary = () => {
    const token = peek();
    if (!token) throw new CalcError('syntax');
    if (token.type === '-') { pos += 1; return { type: 'neg', arg: parseUnary() }; }
    if (token.type === '+') { pos += 1; return parseUnary(); }
    if (token.type === '√') { pos += 1; return { type: 'fn', name: 'sqrt', arg: parseUnary() }; }
    return parsePower();
  };

  const parsePower = () => {
    const base = parsePostfix();
    if (peek() && peek().type === '^') {
      pos += 1;
      return { type: 'bin', op: '^', left: base, right: parseUnary() };
    }
    return base;
  };

  const parsePostfix = () => {
    let node = parsePrimary();
    while (peek() && peek().type === '%') { pos += 1; node = { type: 'pct', arg: node }; }
    return node;
  };

  const parsePrimary = () => {
    const token = peek();
    if (!token) throw new CalcError('syntax');

    if (token.type === 'number') { pos += 1; return { type: 'num', value: token.value }; }

    if (token.type === '(') {
      pos += 1;
      const inner = parseExpr();
      eat(')');
      return inner;
    }

    if (token.type === 'name') {
      pos += 1;
      if (token.value in CONSTANTS) return { type: 'num', value: CONSTANTS[token.value] };
      if (token.value in FUNCTIONS) {
        eat('(');
        const arg = parseExpr();
        eat(')');
        return { type: 'fn', name: token.value, arg };
      }
      throw new CalcError('syntax');
    }

    throw new CalcError('syntax');
  };

  const tree = parseExpr();
  if (pos !== tokens.length) throw new CalcError('syntax');
  return tree;
};

const evaluate = (node) => {
  switch (node.type) {
    case 'num':
      return node.value;
    case 'neg':
      return -evaluate(node.arg);
    case 'pct':
      return evaluate(node.arg) / 100;
    case 'fn':
      return FUNCTIONS[node.name](evaluate(node.arg));
    case 'bin': {
      const left = evaluate(node.left);
      // `a + b%` and `a - b%` read as "b percent of a"; elsewhere a percent is
      // just a hundredth, so the plain evaluation applies.
      if ((node.op === '+' || node.op === '-') && node.right.type === 'pct') {
        const share = left * (evaluate(node.right.arg) / 100);
        return node.op === '+' ? left + share : left - share;
      }
      const right = evaluate(node.right);
      switch (node.op) {
        case '+': return left + right;
        case '-': return left - right;
        case '*': return left * right;
        case '/':
          if (right === 0) throw new CalcError('divzero');
          return left / right;
        case 'mod':
          if (right === 0) throw new CalcError('divzero');
          return left % right;
        case '^': return left ** right;
        default: throw new CalcError('syntax');
      }
    }
    default:
      throw new CalcError('syntax');
  }
};

/** Evaluates an infix expression, throwing CalcError on bad input. */
export const calculate = (expression) => {
  const tokens = tokenize(expression);
  if (tokens.length === 0) throw new CalcError('syntax');
  const value = evaluate(parse(tokens));
  if (!Number.isFinite(value)) throw new CalcError('domain');
  return value;
};

/**
 * Trims binary-float noise (0.1 + 0.2 → 0.3) and falls back to exponential
 * notation only when a plain decimal would be unreadable.
 */
export const formatNumber = (value, locale = 'en') => {
  if (!Number.isFinite(value)) return '∞';
  if (value === 0) return '0';

  const magnitude = Math.abs(value);
  if (magnitude >= 1e15 || magnitude < 1e-9) {
    return value.toExponential(6).replace(/\.?0+e/, 'e');
  }

  const rounded = Number(value.toPrecision(12));
  return rounded.toLocaleString(locale, { maximumFractionDigits: 10, useGrouping: false });
};
